import React, { ReactElement } from "react";
import { Link, useHistory } from "react-router-dom";
import { Table, Image } from "antd";
import { format } from "date-fns";
import { startCase } from "lodash";
import { trackLink } from "../../utils/links";
import { SearchTrack } from "../../api/types/search-results";
import LoadingSpinner from "../loading-spinner";
import styles from "./styles";
import { ChartPlatform } from "../../api/types/charts";

interface Props {
  tracks?: SearchTrack[];
  platform: string;
  isLoading: boolean;
}

const formatChartData = (tracks?: SearchTrack[]) => {
  if (!tracks) {
    return [];
  }
  return tracks.map((track, index) => {
    return {
      rank: index + 1,
      thumbnail: (
        <Image
          css={styles.thumbnail}
          preview={false}
          src={track.imageThumbnail}
        />
      ),
      title: (
        <Link
          data-heap-id="top-tracks"
          data-selected-track={`${track.title} by ${track.artist}`}
          to={trackLink(track)}
        >
          {track.title}
        </Link>
      ),
      artist: track.artist,
      releaseDate: track.releaseDate
        ? format(new Date(track.releaseDate), "MMM dd, yyy")
        : "",
      popularityMetricValue: track.popularityMetricValue?.toLocaleString(),
    };
  });
};

export default function ResultsTable({
  tracks,
  platform,
  isLoading,
}: Props): ReactElement {
  const history = useHistory();

  if (!tracks || tracks.length < 1) {
    return (
      <div css={styles.loading}>
        <LoadingSpinner />
      </div>
    );
  }

  const dataSource = formatChartData(tracks);
  const metricLabel =
    tracks && tracks.length > 0 && startCase(tracks[0].popularityMetricName);

  const columns = [
    {
      title: "",
      dataIndex: "rank",
      key: "rank",
    },
    {
      title: "",
      dataIndex: "thumbnail",
      key: "thumbnail",
    },
    {
      title: "Song",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Artist",
      dataIndex: "artist",
      key: "artist",
    },
    {
      title: "Release Date",
      dataIndex: "releaseDate",
      key: "releaseDate",
    },
    {
      title: metricLabel,
      dataIndex: "popularityMetricValue",
      key: "popularityMetricValue",
    },
  ];

  if (platform === ChartPlatform.youtube) {
    columns.splice(4, 1);
  }

  return (
    <div>
      <Table
        data-heap-id="top-tracks-table"
        rowKey="rank"
        onRow={(record) => {
          return {
            onClick: () => {
              history.push(record.title.props.to);
            },
          };
        }}
        locale={{
          emptyText:
            "Sorry, we couldn't find the top songs for this selection.",
        }}
        css={styles.resultsTable}
        dataSource={dataSource}
        columns={columns}
        loading={isLoading}
      />
    </div>
  );
}
